import React, { ReactElement } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import { BrowserRouter, useNavigate } from 'react-router-dom';
import { ApolloClient, ApolloLink, ApolloProvider, InMemoryCache } from '@apollo/client';
import { createUploadLink } from 'apollo-upload-client';
import { AppState, Auth0Provider, useAuth0 } from '@auth0/auth0-react';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { ModalContextProvider } from 'common/Context/ModalContext';

import auth0 from 'auth0-js';
import { loadScript } from 'utils/loadScript';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { getEnvironmentVariables } from './services/environment-variables/environment-variables';

const env = getEnvironmentVariables();

const FEATURE_TOGGLES = ['pauseStoreEnabled'];

export const cache = new InMemoryCache({
  typePolicies: {
    Product: { keyFields: ['mmId'] },
    Menu: { keyFields: ['mmId'] },
    Venue: { keyFields: ['hashedId'] },
    User: { keyFields: ['id'] },
    BundledItem: { keyFields: ['mmId', 'includedItems'] },
    IncludedItem: { keyFields: ['mmId'] },
    MenuVariant: { keyFields: ['mmId'] },
    Features: { keyFields: FEATURE_TOGGLES }
  }
});

export const client = new ApolloClient({
  uri: env.APOLLO_URL,
  cache
});

interface Auth0ProviderWithHistoryProps {
  children: ReactElement;
}

const webAuth = new auth0.WebAuth({
  domain: env.AUTH0_DOMAIN,
  clientID: env.AUTH0_CLIENT_ID
});

const Auth0ProviderWithHistory = (props: Auth0ProviderWithHistoryProps): JSX.Element => {
  const { children } = props;
  const navigate = useNavigate();

  const onRedirectCallback = (appState: AppState | undefined): void => {
    const storedAuth0StateVerify = localStorage.getItem('auth0StateVerify');
    if (storedAuth0StateVerify && storedAuth0StateVerify === appState?.stateVerify) {
      localStorage.removeItem('auth0StateVerify');
      navigate(appState?.returnTo || window.location.pathname);
    } else {
      navigate('/');
    }
  };

  return (
    <Auth0Provider
      domain={env.AUTH0_DOMAIN}
      clientId={env.AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin
      }}
      useRefreshTokens
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
};

const checkSessionPromise = async (): Promise<PromiseConstructor> =>
  new Promise((resolve, reject) => {
    webAuth.checkSession(
      {
        responseType: 'token id_token',
        redirectUri: window.location.origin
      },
      (err, authResult) => {
        if (authResult) {
          resolve(authResult);
        } else {
          reject(err);
        }
      }
    );
  });

const AuthorizedApolloProvider = ({ children }: { children: ReactElement }): JSX.Element => {
  const { getAccessTokenSilently, logout, isAuthenticated } = useAuth0();

  const httpLink = createUploadLink({
    uri: env.APOLLO_URL
  });

  const authLink = setContext(async (_, { headers }) => {
    if (isAuthenticated) {
      let token;
      try {
        await checkSessionPromise();
        token = await getAccessTokenSilently();
      } catch (e) {
        logout({
          logoutParams: {
            federated: true
          }
        });
      }

      return {
        headers: {
          ...headers,
          'Apollo-Require-Preflight': 'true',
          authorization: token ? `Bearer ${token}` : ''
        }
      };
    }
    return {
      headers: {
        ...headers,
        'Apollo-Require-Preflight': 'true'
      }
    };
  });

  const authError = onError(({ graphQLErrors }) => {
    if (graphQLErrors) {
      const authError = graphQLErrors.find(({ extensions }) => extensions?.mmCode === 3001);
      if (authError) {
        logout({ logoutParams: { federated: true } });
      }
    }
  });

  const link = ApolloLink.from([authLink, authError, httpLink]);

  const apolloClient = new ApolloClient({
    uri: env.APOLLO_URL,
    cache,
    link,
    connectToDevTools: true
  });

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <BrowserRouter>
    <ModalContextProvider>
      <Auth0ProviderWithHistory>
        <AuthorizedApolloProvider>
          <App />
        </AuthorizedApolloProvider>
      </Auth0ProviderWithHistory>
    </ModalContextProvider>
  </BrowserRouter>
);

// load Hubspot tracking script
if (env.HUBSPOT_TRACKING_ID)
  loadScript({
    src: `https://js.hs-scripts.com/${env.HUBSPOT_TRACKING_ID}.js`,
    async: true,
    defer: true,
    id: 'hs-script-loader'
  });

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
